import { Component, ReactNode } from "react";
import { i18next } from "../i18n";
import "./ErrorBoundary.css";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Displays fallback UI instead of crashing the whole app
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // TODO: In production, log to error reporting service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">{i18next.t("errors.errorBoundaryTitle")}</h2>
            <p className="error-boundary-message">{i18next.t("errors.errorBoundaryMessage")}</p>

            <details className="error-boundary-details">
              <summary>{i18next.t("errors.errorDetails")}</summary>
              <pre>{this.state.error.toString()}</pre>
              {this.state.error.stack && (
                <pre className="error-boundary-stack">{this.state.error.stack}</pre>
              )}
            </details>

            <div className="error-boundary-actions">
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
                aria-label={i18next.t("common.reloadPage")}
              >
                {i18next.t("common.reloadPage")}
              </button>
              <button
                className="btn btn-secondary"
                onClick={this.handleReset}
                aria-label={i18next.t("common.retry")}
              >
                {i18next.t("common.retry")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
